// src/services/textPost.js — the look of a text-only post.
//
// ══ ONE CATALOGUE, TWO PLATFORMS ═══════════════════════════════════════════
// A styled post stores only IDs and flags, never colours or CSS:
//
//     textStyle: {
//       bg: 'dusk',          // an id from BACKGROUNDS
//       doodle: 'confetti',  // an id from DOODLES, or null
//       bold: true,
//       underline: false,
//       size: 'lg',          // sm | md | lg
//     }
//
// 🟥 IDS, NOT VALUES, AND THE REASON MATTERS. Storing the actual gradient would
//    freeze every post to the palette that existed the day it was written — one
//    tweak to a colour and old posts and new posts stop matching, forever. Ids
//    mean the catalogue can be re-tuned and every post follows. It also means
//    the phone and the browser cannot drift apart: allo-app carries this same
//    file with the same ids, so a post composed on either renders identically on
//    both. Change a colour here, change it there.
//
// An unknown id degrades to plain text rather than throwing — a post from a
// future version of the app must still be readable on an older client.

/* ── backgrounds ─────────────────────────────────────────────────────────────
   `css` is the web's gradient. `colors` is the same ramp as plain stops, which
   is what react-native-linear-gradient needs — keeping both here is what stops
   the two platforms being tuned separately.
   `ink` is the text colour, chosen for contrast against that ramp, never
   derived at runtime. */
export const BACKGROUNDS = [
  {
    id: 'plain', name: 'None',
    css: null, colors: null, ink: null,
  },
  {
    id: 'dusk', name: 'Dusk',
    css: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
    colors: ['#6a11cb', '#2575fc'], ink: '#ffffff',
  },
  {
    id: 'ember', name: 'Ember',
    css: 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)',
    colors: ['#f5576c', '#f093fb'], ink: '#ffffff',
  },
  {
    id: 'lagoon', name: 'Lagoon',
    css: 'linear-gradient(135deg, #0ba360 0%, #3cba92 100%)',
    colors: ['#0ba360', '#3cba92'], ink: '#ffffff',
  },
  {
    id: 'mango', name: 'Mango',
    css: 'linear-gradient(135deg, #ff8008 0%, #ffc837 100%)',
    colors: ['#ff8008', '#ffc837'], ink: '#20160a',
  },
  {
    id: 'ink', name: 'Ink',
    css: 'linear-gradient(135deg, #232526 0%, #414345 100%)',
    colors: ['#232526', '#414345'], ink: '#ffffff',
  },
  {
    id: 'cotton', name: 'Cotton',
    css: 'linear-gradient(135deg, #ffdee9 0%, #b5fffc 100%)',
    colors: ['#ffdee9', '#b5fffc'], ink: '#1b2430',
  },
  {
    id: 'midnight', name: 'Midnight',
    css: 'linear-gradient(135deg, #0f2027 0%, #203a43 55%, #2c5364 100%)',
    colors: ['#0f2027', '#203a43', '#2c5364'], ink: '#ffffff',
  },
  {
    id: 'rose', name: 'Rose',
    css: 'linear-gradient(135deg, #ee9ca7 0%, #ffdde1 100%)',
    colors: ['#ee9ca7', '#ffdde1'], ink: '#3a2027',
  },
];

export const bgById = (id) => BACKGROUNDS.find((b) => b.id === id) || BACKGROUNDS[0];

/* ── doodles ─────────────────────────────────────────────────────────────────
   Each is a tiling SVG returned as a STRING, so the web can make it a data URI
   and react-native-svg can render the identical markup. No image assets to ship,
   no two copies to keep in step, and it stays crisp at any size.
   `ink` is passed in so a doodle picks up the background's text colour. */
const DOODLE_SVG = {
  confetti: (c) => `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">
    <g fill="none" stroke="${c}" stroke-width="2.4" stroke-linecap="round" opacity="0.5">
      <path d="M8 12 l6 -6"/><path d="M46 8 l5 6"/><path d="M24 30 l7 -4"/>
      <path d="M52 38 l-6 5"/><path d="M12 48 l5 5"/><path d="M36 54 l6 -3"/>
    </g>
    <g fill="${c}" opacity="0.45">
      <circle cx="30" cy="12" r="2.1"/><circle cx="56" cy="26" r="1.8"/>
      <circle cx="10" cy="34" r="1.9"/><circle cx="42" cy="42" r="2.1"/>
      <circle cx="22" cy="60" r="1.7"/>
    </g></svg>`,
  hearts: (c) => `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">
    <g fill="${c}" opacity="0.4">
      <path d="M16 22c-4-4-9-1-9 3 0 5 9 10 9 10s9-5 9-10c0-4-5-7-9-3z"/>
      <path d="M46 48c-3-3-7-1-7 2.3 0 3.9 7 7.7 7 7.7s7-3.8 7-7.7c0-3.3-4-5.3-7-2.3z"/>
    </g></svg>`,
  stars: (c) => `<svg xmlns="http://www.w3.org/2000/svg" width="72" height="72" viewBox="0 0 72 72">
    <g fill="${c}" opacity="0.42">
      <path d="M18 8l2.2 5.4L26 15.6l-5.8 2.2L18 23.6l-2.2-5.8L10 15.6l5.8-2.2z"/>
      <path d="M52 34l1.7 4.2L58 39.9l-4.3 1.7L52 45.9l-1.7-4.3L46 39.9l4.3-1.7z"/>
      <path d="M30 56l1.4 3.4L35 60.8l-3.6 1.4L30 65.6l-1.4-3.4L25 60.8l3.6-1.4z"/>
    </g></svg>`,
  waves: (c) => `<svg xmlns="http://www.w3.org/2000/svg" width="80" height="40" viewBox="0 0 80 40">
    <g fill="none" stroke="${c}" stroke-width="2" stroke-linecap="round" opacity="0.35">
      <path d="M0 12c10 -8 20 8 30 0s20 -8 30 0 20 8 20 0"/>
      <path d="M0 30c10 -8 20 8 30 0s20 -8 30 0 20 8 20 0"/>
    </g></svg>`,
  grid: (c) => `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
    <g fill="${c}" opacity="0.3"><circle cx="4" cy="4" r="1.6"/><circle cx="24" cy="14" r="1.6"/>
    <circle cx="14" cy="30" r="1.6"/><circle cx="34" cy="34" r="1.6"/></g></svg>`,
  sparkle: (c) => `<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 60 60">
    <g fill="none" stroke="${c}" stroke-width="2" stroke-linecap="round" opacity="0.42">
      <path d="M14 6v10M9 11h10"/><path d="M44 26v8M40 30h8"/><path d="M24 44v7M20.5 47.5h7"/>
    </g></svg>`,
};

export const DOODLES = [
  { id: null, name: 'None' },
  { id: 'confetti', name: 'Confetti' },
  { id: 'hearts', name: 'Hearts' },
  { id: 'stars', name: 'Stars' },
  { id: 'waves', name: 'Waves' },
  { id: 'grid', name: 'Dots' },
  { id: 'sparkle', name: 'Sparkle' },
];

/**
 * The raw SVG markup for a doodle — the app renders this string directly.
 *
 * 🟥 `opacity` EXISTS BECAUSE A TILE AND AN ICON ARE NOT THE SAME DRAWING. The
 *    values baked into the markup above — 0.3 to 0.5 — are tuned for wallpaper
 *    BEHIND a poster's words. A picker draws the very same markup at 15 points
 *    as the only content of a chip, and at 30% alpha that is a smudge you
 *    cannot make out in either theme. Passing 1 overrides every baked value;
 *    passing nothing keeps the catalogue's own, so the poster is untouched.
 *    Twin of app/src/components/textPostStyles.ts — same signature, same rule.
 */
export function doodleSvg(id, ink, opacity) {
  const make = DOODLE_SVG[id];
  if (!make) return null;
  const svg = make(ink || '#ffffff');
  if (opacity === undefined || opacity === null) return svg;
  const a = Math.max(0, Math.min(1, opacity));
  return svg.replace(/opacity="[0-9.]+"/g, `opacity="${a}"`);
}

/** The same markup as a CSS-ready data URI. */
export function doodleUrl(id, ink, opacity) {
  const svg = doodleSvg(id, ink, opacity);
  if (!svg) return null;
  return `url("data:image/svg+xml,${encodeURIComponent(svg.replace(/\s+/g, ' ').trim())}")`;
}

/* ── size ────────────────────────────────────────────────────────────────────
   One scale, three stops. `web` is px for the browser; `app` is the RN value.
   They differ on purpose — RN points and CSS pixels are not the same unit, and
   pretending they are is how "the same" post ends up looking bigger on one. */
export const SIZES = [
  { id: 'sm', name: 'Small', web: 20, app: 19 },
  { id: 'md', name: 'Medium', web: 28, app: 26 },
  { id: 'lg', name: 'Large', web: 38, app: 34 },
];

export const sizeById = (id) => SIZES.find((s) => s.id === id) || SIZES[1];

export const DEFAULT_TEXT_STYLE = {
  bg: 'plain', doodle: null, bold: false, underline: false, size: 'md',
};

/**
 * Should this post render as a styled text card?
 *
 * 🟥 ONLY WHEN THERE IS NOTHING ELSE IN IT. A background behind a caption that
 *    also has a photo would fight the photo and win, so a styled card is
 *    strictly for a post whose whole content IS the words. Media, or a link
 *    card, and it renders as an ordinary post no matter what is stored.
 */
export function isStyledText(post) {
  if (!post?.textStyle) return false;
  if (post.linkUrl) return false;
  /* 🟥 A POLL IS NEVER A STYLED TEXT POST, and this is the reader half of that
     rule. A poll's caption used to trigger a background while it was being
     typed, and the published post then drew the gradient card WITH the question
     in it and the poll's own question and options again underneath — the same
     words twice, in two different designs. A poll is the post; the caption is a
     caption. */
  if (post.type === 'poll' || (Array.isArray(post.pollOptions) && post.pollOptions.length > 0)) return false;
  const hasMedia = (post.media || []).length > 0 || post.imageUrl || post.videoUrl;
  if (hasMedia) return false;
  // 'plain' with no doodle and no emphasis is just a text post — say so
  return !isQuietStyle(post.textStyle);
}

/**
 * Is this "style" actually a style, or is it the untouched default?
 *
 * 🟢 THE SAME CONDITION `isStyledText` ASKS, extracted rather than restated, so
 *    the composer and the feed cannot end up with two ideas of what unstyled
 *    means. `quiet` here and `isStyledText` there are exact opposites — asserted
 *    over a matrix in gtest rather than assumed.
 *
 * 🟥 WHY THE COMPOSER NEEDS TO ASK. The editor is the same card the feed draws,
 *    which is deliberate — a preview drawn by different code eventually lies.
 *    But a card with nothing chosen yet is not a poster, and rendering the
 *    invitation "Share a vibe…" at 26px centred in a 120px box made an empty
 *    composer the loudest thing on the page.
 */
export function isQuietStyle(style) {
  if (!style) return true;
  return bgById(style.bg).id === 'plain' && !style.doodle
    && !style.bold && !style.underline && style.size === 'md';
}

/** Everything the web needs to draw one, in one object. */
export function textPostCss(style) {
  const st = { ...DEFAULT_TEXT_STYLE, ...(style || {}) };
  const bg = bgById(st.bg);
  const size = sizeById(st.size);
  const ink = bg.ink || 'var(--text)';
  const layers = [];
  const doodle = doodleUrl(st.doodle, bg.ink || '#7b8494');
  if (doodle) layers.push(doodle);
  if (bg.css) layers.push(bg.css);

  /* 🟥 COMPUTED FROM THE STYLE AS GIVEN, NOT THE MERGED ONE, so that `quiet`
     is exactly `!isStyledText` — the same input that decides whether the feed
     draws this card at all. Reading the merged copy would let the two answers
     differ for a style that omits a field, which is the sort of divergence that
     shows up as one post rendering differently in two places. */
  const quiet = isQuietStyle(style);

  return {
    wrap: {
      background: layers.length ? layers.join(', ') : undefined,
      color: bg.ink || undefined,
    },
    text: {
      fontSize: size.web,
      lineHeight: 1.28,
      fontWeight: st.bold ? 800 : 600,
      textDecoration: st.underline ? 'underline' : 'none',
      textUnderlineOffset: st.underline ? '6px' : undefined,
      color: ink,
    },
    plain: bg.id === 'plain',
    quiet,
  };
}

/* ══ WHEN A BACKGROUND IS APPROPRIATE ══════════════════════════════════════
   Backgrounds are for a line or two — a thought, a mood, a question. Past that
   they stop being a design and start being a wall of huge text on a gradient
   that is harder to read than plain words on paper. Every app that offers them
   draws the same line: Facebook and Instagram both withdraw the option once a
   post grows past roughly a sentence or two.

   🟥 AND THE WITHDRAWAL HAS TO BE SILENT AND REVERSIBLE. Someone typing past the
      limit has not made a mistake and must not be told off — the card simply
      becomes a normal text box, and if they delete back under the limit it
      comes back with their choices intact. Nothing is lost either way.

   Three ways a post stops qualifying:
     · it has media — the background would fight the photo and win
     · it is long — MAX_CHARS
     · it is several sentences — MAX_SENTENCES, which catches three short lines
       that slip under the character count */
export const STYLE_MAX_CHARS = 130;
export const STYLE_MAX_SENTENCES = 2;

export function sentenceCount(text) {
  const t = String(text || '').trim();
  if (!t) return 0;
  // a run of . ! ? or a line break ends a sentence; trailing punctuation doesn't
  // count as an empty extra one
  return t.split(/[.!?\n]+/).map((x) => x.trim()).filter(Boolean).length;
}

/** Can this draft wear a background right now? */
export function canStyleText(text, hasMedia, hasPoll) {
  /* 🟥 A POLL TAKES THE OPTION AWAY, for the same reason media does: the
     background is for a post whose whole body is the words. A poll's body is
     the question and its options, and the caption above them is a caption. */
  if (hasPoll) return false;
  if (hasMedia) return false;
  const t = String(text || '');
  if (!t.trim()) return false;
  if (t.length > STYLE_MAX_CHARS) return false;
  if (sentenceCount(t) > STYLE_MAX_SENTENCES) return false;
  return true;
}

/** The one a new post starts on, so a background is offered rather than hunted for. */
export const SUGGESTED_BG = 'dusk';
